import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";

import { departureOptions } from "../../../lib/rateCardUtils";
import {
  DEPARTURE,
  LOCAL_DATETIME,
  toLocalInput as toLocal,
} from "../departure";

/**
 * When the guest says they will leave, and what that does to the booking.
 */
export function initialMode(initial, options, requireExact = false) {
  if (initial?.expectedDeparture) return DEPARTURE.SCHEDULED;
  /* The desk now has to say when the guest leaves, unless this card is one of
   * the few that lets the answer stay unknown. */
  if (requireExact && options?.allowScheduled) return DEPARTURE.SCHEDULED;

  return DEPARTURE.DEFAULT;
}

export default function useDeparture({
  rateCard,
  tz,
  initial,
  startDay,
  endDay,
  setEndDay,
  startDateTime,
  endDateTime,
  expiresAt,
  requireExact = false,
}) {
  const options = useMemo(() => departureOptions(rateCard), [rateCard]);

  const [mode, setMode] = useState(() =>
    initialMode(initial, options, requireExact),
  );
  const [at, setAt] = useState(() => toLocal(initial?.expectedDeparture, tz));

  const seededAt = useRef(Boolean(at));

  useEffect(() => {
    if (seededAt.current || !tz || !initial?.expectedDeparture) return;

    seededAt.current = true;
    setAt(toLocal(initial.expectedDeparture, tz));
  }, [tz, initial]);

  /* An hourly card sells time, not a slot: its endDay is an access window, so
   * there is no card exit to name on the tile. Accepting the default there
   * means the desk does not know when the guest leaves - what UNKNOWN used to
   * say, before the two collapsed into the one answer they always were. */
  const hasCardExit =
    rateCard?.type !== "HOURLY" && rateCard?.type !== "TIERED_HOURS";

  const hasMovableExitDate =
    (rateCard?.type === "DAILY" || rateCard?.type === "FLEXIBLE") &&
    rateCard?.calculationMode !== "MULTI_DATE_FIXED_PACKAGE" &&
    rateCard?.calculationMode !== "SINGLE_DAY" &&
    rateCard?.type !== "MONTHLY";

  const exitDerivedFromEntry = rateCard?.calculationMode === "SINGLE_DAY";

  useEffect(() => {
    if (!exitDerivedFromEntry || !startDay) return;
    if (endDay === startDay) return;

    setEndDay(startDay);
  }, [exitDerivedFromEntry, startDay, endDay, setEndDay]);

  /*
   * The earliest exit date that means anything on this card.
   */
  const requiresOvernightBump =
    (rateCard?.calculationMode === "MULTI_DATE_OVERNIGHT" ||
      rateCard?.calculationMode === "LEGACY") &&
    (rateCard?.entryAfterTime ?? 0) >= (rateCard?.exitBeforeTime ?? 0);

  const minEndDay = useMemo(() => {
    if (!startDay || !tz || !requiresOvernightBump) return startDay;

    const dt = DateTime.fromISO(startDay, { zone: tz });
    return dt.isValid ? dt.plus({ days: 1 }).toFormat("yyyy-MM-dd") : startDay;
  }, [startDay, tz, requiresOvernightBump]);

  /* Keep the selection legal when the operator switches guest card. */
  useEffect(() => {
    if (!rateCard) return;

    setMode((current) => {
      if (current === DEPARTURE.SCHEDULED && options.allowScheduled) return current;
      if (requireExact && options.allowScheduled) return DEPARTURE.SCHEDULED;
      /* Every card offers the default, so everything else lands there. */
      return DEPARTURE.DEFAULT;
    });
  }, [rateCard, options, requireExact]);

  useEffect(() => {
    if (!hasMovableExitDate || !minEndDay) return;
    if (endDay >= minEndDay) return;

    setEndDay(minEndDay);
  }, [hasMovableExitDate, endDay, minEndDay, setEndDay]);

  useEffect(() => {
    if (!hasMovableExitDate) return;
    if (mode !== DEPARTURE.SCHEDULED || !at) return;

    /* datetime-local is already wall-clock in the facility's zone. */
    const exitDay = at.slice(0, 10);
    if (exitDay.length !== 10) return;

    /* Never below the shortest window this card can sell. */
    setEndDay(exitDay < minEndDay ? minEndDay : exitDay);
  }, [hasMovableExitDate, mode, at, minEndDay, setEndDay]);

  /* A time-only pickup is pinned to the entry's own day — only the clock is
   * the guest's answer — so the day is carried across whenever the entry
   * moves, forwards or back. */
  useEffect(() => {
    if (!options.scheduledTimeOnly || mode !== DEPARTURE.SCHEDULED) return;
    if (!at || !startDay || at.slice(0, 10) === startDay) return;

    setAt(`${startDay}${at.slice(10)}`);
  }, [options, mode, at, startDay]);

  const expectedDepartureAt = useMemo(() => {
    if (mode !== DEPARTURE.SCHEDULED || !at || !tz) return null;

    const dt = DateTime.fromISO(at, { zone: tz });
    return dt.isValid ? dt.toISO() : null;
  }, [mode, at, tz]);

  /*
   * The picker's bounds.
   */
  const bounds = useMemo(() => {
    if (!tz) return {};

    const local = (iso) => {
      const dt = DateTime.fromISO(iso, { zone: tz });
      return dt.isValid ? dt.toFormat(LOCAL_DATETIME) : undefined;
    };

    const min = startDateTime ? local(startDateTime) : undefined;

    if (options.scheduledTimeOnly && startDay) {
      return {
        min,
        max: DateTime.fromISO(startDay, { zone: tz })
          .endOf("day")
          .toFormat(LOCAL_DATETIME),
      };
    }

    if (hasMovableExitDate || !expiresAt) return { min };

    const expiry = DateTime.fromISO(expiresAt, { zone: tz });
    return {
      min,
      max: expiry.isValid
        ? expiry.minus({ minutes: 1 }).toFormat(LOCAL_DATETIME)
        : undefined,
    };
  }, [tz, startDateTime, startDay, options, hasMovableExitDate, expiresAt]);

  const outOfRange = Boolean(
    mode === DEPARTURE.SCHEDULED &&
      at &&
      ((bounds.max && at > bounds.max) || (bounds.min && at < bounds.min)),
  );

  const valid =
    mode !== DEPARTURE.SCHEDULED ||
    (Boolean(expectedDepartureAt) && !outOfRange);

  /* Whether the pickup runs past the exit time on its own day — the only case
   * where covering the pickup day still leaves something to charge. */
  const overruns = Boolean(
    expectedDepartureAt &&
      endDateTime &&
      new Date(expectedDepartureAt) > new Date(endDateTime),
  );

  const scheduledExitLabel = useMemo(() => {
    if (!endDateTime || !tz) return null;

    const dt = DateTime.fromISO(endDateTime, { zone: tz });
    return dt.isValid ? dt.toFormat("HH:mm, ccc d LLL") : null;
  }, [endDateTime, tz]);

  const tileLabel = useMemo(() => {
    if (!endDateTime || !tz) return null;

    const dt = DateTime.fromISO(endDateTime, { zone: tz });
    if (!dt.isValid) return null;

    const days = dt
      .startOf("day")
      .diff(DateTime.now().setZone(tz).startOf("day"), "days").days;

    if (days === 0) return `By ${dt.toFormat("HH:mm")} today`;
    if (days === 1) return `By ${dt.toFormat("HH:mm")} tomorrow`;

    return `By ${dt.toFormat("HH:mm, ccc d LLL")}`;
  }, [endDateTime, tz]);

  const tiles = useMemo(() => {
    const list = [];

    if (options.allowDefault) {
      list.push({
        id: DEPARTURE.DEFAULT,
        label: hasCardExit ? tileLabel ?? "Card default" : "Mark unknown",
        hint: hasCardExit
          ? "Scheduled exit on this guest card"
          : "Guest confirms at pickup",
      });
    }

    if (options.allowScheduled) {
      list.push({
        id: DEPARTURE.SCHEDULED,
        label: options.scheduledTimeOnly ? "Pick a time" : "Pick date & time",
        hint: "Guest knows exact pickup",
      });
    }

    return list;
  }, [options, tileLabel, hasCardExit]);

  /* The arrival moved onto another day. The pickup is the guest's own answer,
   * so it only follows when the arrival has run past it — and then by whole
   * days, so the stay keeps both its length and its pickup time. */
  const followArrival = (prevArrivalAt, nextArrivalAt) => {
    if (mode !== DEPARTURE.SCHEDULED || !at || !tz) return;
    if (!prevArrivalAt || !nextArrivalAt) return;
    /* Already ridden across by the entry day it is pinned to. */
    if (options.scheduledTimeOnly) return;

    const prevDay = prevArrivalAt.slice(0, 10);
    const nextDay = nextArrivalAt.slice(0, 10);
    if (prevDay.length !== 10 || nextDay.length !== 10) return;
    if (prevDay === nextDay) return;

    /* Still room before the pickup, so leave the guest's answer alone. */
    if (nextArrivalAt < at) return;

    const from = DateTime.fromISO(prevDay, { zone: tz });
    const to = DateTime.fromISO(nextDay, { zone: tz });
    if (!from.isValid || !to.isValid) return;

    const days = to.diff(from, "days").days;
    if (days <= 0) return;

    const shifted = DateTime.fromISO(at, { zone: tz }).plus({ days });
    if (shifted.isValid) setAt(shifted.toFormat(LOCAL_DATETIME));
  };

  /* Open the picker on something plausible: the card's own exit where it has
   * one, otherwise a couple of hours from now for an hourly stay. */
  const seedAt = () => {
    if (at || !tz) return;
    /* The booking's own pickup is still on its way in; seeding now would
     * overwrite it a render later. */
    if (initial?.expectedDeparture && !seededAt.current) return;

    const soonest = DateTime.now().setZone(tz).plus({ hours: 2 }).startOf("hour");
    const carded =
      options.allowDefault && endDateTime
        ? DateTime.fromISO(endDateTime, { zone: tz })
        : null;

    const seed = carded?.isValid && carded > soonest ? carded : soonest;

    if (seed.isValid) setAt((current) => current || seed.toFormat(LOCAL_DATETIME));
  };

  /* The card may have moved the mode here without anyone pressing a tile. */
  useEffect(() => {
    if (mode !== DEPARTURE.SCHEDULED) return;

    seedAt();
  });

  const select = (next) => {
    setMode(next);

    if (next !== DEPARTURE.SCHEDULED) return;

    seedAt();
  };

  return {
    mode,
    at,
    setAt,
    select,
    followArrival,
    options,
    tiles,
    bounds,
    minEndDay,
    hasMovableExitDate,
    exitDerivedFromEntry,
    expectedDepartureAt,
    scheduledExitLabel,
    outOfRange,
    overruns,
    valid,
  };
}
